import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { createCoalescedRunner } from '@/renderer/utils/coalesce';
import { useAuthToken } from '@/renderer/utils/useMe';
import type { UseRequestResult } from '@/renderer/utils/useRequest';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';

// A grade updates the play row live, but the record in the header, the season/sport
// breakdown and the chart are separate stats requests that nothing refetched, so the
// row read "Win" while the season row stayed a game behind (6-6 instead of 7-6).
// The server broadcasts `release/updated` { id, result } on every release change;
// a graded result bumps a revision here, and each stats request in the Results view
// refetches on it (bypassing the client cache).
//
// A slate is graded in bursts, so the revision bump is debounced into one trailing
// refetch, and jittered so every viewer does not refetch in lockstep off the same
// broadcast (the #2059 lesson).
export const LIVE_STATS_DEBOUNCE_MS = 2000;
export const LIVE_STATS_JITTER_MS = 1500;

type ReleaseUpdatedEvent = { id?: API.Id; result?: string } | API.Id | null | undefined;

/** Whether a `release/updated` payload is a grade or regrade (not a pending edit). */
export function isGradeEvent(data: ReleaseUpdatedEvent): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  return !!data.result && data.result !== 'pending';
}

/**
 * The Results view's grade revision: 0 until the first live grade, then +1 per
 * debounced burst of grades. Mount once per Results view.
 */
export function useGradeRevision(): number {
  const authToken = useAuthToken();
  const [revision, setRevision] = useState(0);

  const runner = useMemo(
    () => createCoalescedRunner(LIVE_STATS_DEBOUNCE_MS, LIVE_STATS_JITTER_MS),
    [],
  );
  useEffect(() => runner.register(() => setRevision((r) => r + 1)), [runner]);

  const onReleaseUpdated = useCallback(
    (_event: MessageEvent, json: { data?: ReleaseUpdatedEvent }) => {
      if (isGradeEvent(json?.data)) {
        runner.schedule();
      }
    },
    [runner],
  );

  useWebSocket(
    WS_BASE_URL,
    useMemo(
      () => ({
        connect: !!authToken,
        subscriptions: { 'release/updated': onReleaseUpdated },
      }),
      [authToken, onReleaseUpdated],
    ),
  );

  return revision;
}

/** Carries the Results view's {@link useGradeRevision} down to its stats requests. */
export const GradeRevisionContext = createContext(0);

type Refetchable = Pick<UseRequestResult<unknown, void>, 'run' | 'dispatch'>;

/**
 * Refetch these stats requests whenever the Results view's grade revision moves.
 * Paused requests (`active: false`) are skipped. The data is swapped in place, with
 * no loading state, so the record ticks over without the card flashing a skeleton.
 * `revision` overrides the context, for the component that owns the revision.
 */
export function useRefetchOnGrade(
  requests: { request: Refetchable; active: boolean }[],
  revisionOverride?: number,
) {
  const contextRevision = useContext(GradeRevisionContext);
  const revision = revisionOverride ?? contextRevision;

  // The requests as of the latest render; the effect fires on the revision only.
  const requestsRef = useRef(requests);
  requestsRef.current = requests;

  useEffect(() => {
    if (!revision) {
      return;
    }
    for (const { request, active } of requestsRef.current) {
      if (!active) {
        continue;
      }
      request
        .run(undefined, { bypassCache: true, dontAlterState: true })
        .then((data) => {
          if (data !== null && data !== undefined) {
            request.dispatch({ action: 'SET', values: { data, error: null } });
          }
        })
        .catch(() => {
          // Best-effort: the next grade or a navigation will refetch.
        });
    }
  }, [revision]);
}
